/**
 * Public service detail page (/services/<slug>). Statically generated for every Starbase service
 * that has a slug. Content is service-level; price hydrates to the visitor's market client-side.
 */
import React, { useEffect, useMemo } from 'react'
import Head from 'next/head'
import styled from '@emotion/styled'
import { loadMarkets, fetchDetailServices } from '../../services/GraphQlClientService'
import { getFaqsByTag, getReviewsByTag } from '../../services/strapi'
import { useMarketServices } from '../../helpers/useMarketServices'
import { trackServiceDetailViewed } from '../../helpers/tracking'
import {
  HeroBookingCard, FeaturedAddons, ThingsToKnow, BeautyProsYouCanTrust,
  CrossSell, ReviewsSection, FaqSection, AppDownload,
} from '../../components/service-detail'

const CANONICAL_ORIGIN = 'https://glamsquad.com'

/**
 * Enumerates every static path for this page from all base services. Market-independent —
 * page content does not depend on a market.
 * @returns {Promise<{ paths: Array<{ params: { serviceSlug: string } }>, fallback: string }>}
 */
export async function getStaticPaths() {
  try {
    const services = await fetchDetailServices()
    const slugs = [...new Set(services.map(s => s.slug).filter(Boolean))]
    return { paths: slugs.map(serviceSlug => ({ params: { serviceSlug } })), fallback: 'blocking' }
  } catch {
    return { paths: [], fallback: 'blocking' }
  }
}

/**
 * Builds props for a single service detail page: the matched service, a cross-sell list
 * of other popular services, the cities where GLAMSQUAD operates, FAQs tagged for this
 * slug, and the service-level featured add-ons.
 *
 * A fetch failure is allowed to throw rather than 404: during ISR revalidation Next keeps
 * serving the last successfully generated page and retries, so a transient backend blip
 * never replaces a live page with a 404. notFound is reserved for a genuinely missing slug.
 * @param {{ params: { serviceSlug: string } }} context
 */
export async function getStaticProps({ params }) {
  const services = await fetchDetailServices()
  const service = services.find(s => s.slug === params.serviceSlug)
  if (!service) {
    return { notFound: true, revalidate: 60 }
  }

  const crossSellServices = services
    .filter(s => s.isCrossSell && s.id !== service.id)
    .map(s => ({
      id: s.id, slug: s.slug, name: s.name,
      blurb: s.description || '',
      price: s.price, duration: s.duration,
      isInformational: s.isInformational, isFlatRate: s.isFlatRate,
      isDurationAdjustable: s.isDurationAdjustable, serviceType: s.serviceType,
    }))

  let availableCities = []
  try {
    const markets = await loadMarkets()
    availableCities = markets.filter(m => m.isEnabled).map(m => m.name)
  } catch {
    availableCities = []
  }

  const featuredAddOns = (service.addOnServices || [])
    .filter(a => a.mobileFeaturedImage)
    .map(a => ({
      id: a.id,
      name: a.addOnName || a.name || '',
      description: a.addOnDescription || '',
      image: a.mobileFeaturedImage,
      price: a.addOnPrice ?? 0,
      duration: a.addOnDuration ?? 0,
    }))

  let faqs = []
  try {
    faqs = (await getFaqsByTag(params.serviceSlug)) || []
  } catch {
    faqs = []
  }

  let reviews = []
  try {
    reviews = (await getReviewsByTag(params.serviceSlug)) || []
  } catch {
    reviews = []
  }

  return {
    props: { service, crossSellServices, availableCities, faqs, featuredAddOns, reviews },
    revalidate: 3600,
  }
}

export default function ServiceDetailPage({ service, crossSellServices, availableCities, faqs, featuredAddOns, reviews = [] }) {
  useEffect(() => {
    trackServiceDetailViewed({ serviceId: service.id, serviceName: service.name, serviceSlug: service.slug })
  }, [service.id, service.name, service.slug])

  const { marketServices, market } = useMarketServices()
  const { visibleCrossSell, visibleAddOns } = useMemo(() => {
    if (!marketServices) {
      return { visibleCrossSell: crossSellServices, visibleAddOns: featuredAddOns }
    }
    const offeredServiceIds = new Set(marketServices.map(ms => ms.service?.id).filter(Boolean))
    const current = marketServices.find(ms => ms.service?.id === service.id)
    const currentAddOns = current?.addOnMarketServices || []
    const offeredAddOnIds = new Set(currentAddOns.map(a => a.service?.id).filter(Boolean))
    const showDisplayPrice = market?.priceDisplay?.toLowerCase() !== 'base'
    const addOnPriceById = new Map()
    currentAddOns.forEach(a => {
      const id = a.service?.id
      if (!id) {
        return
      }
      const price = showDisplayPrice ? a.displayPrice : a.service?.addOnPrice
      if (price != null) {
        addOnPriceById.set(id, price)
      }
    })
    return {
      visibleCrossSell: crossSellServices.filter(s => offeredServiceIds.has(s.id)),
      visibleAddOns: featuredAddOns
        .filter(a => offeredAddOnIds.has(a.id))
        .map(a => (addOnPriceById.has(a.id) ? { ...a, price: addOnPriceById.get(a.id) } : a)),
    }
  }, [marketServices, market, service.id, crossSellServices, featuredAddOns])

  const title = `${service.name} at Home | GLAMSQUAD`
  const description = service.infoTagline || service.description || `Book a ${service.name} at home with GLAMSQUAD.`
  const url = `${CANONICAL_ORIGIN}/services/${service.slug}`
  // Starbase image URLs point at raw S3 storage — route them through the Next image
  // optimizer so crawlers get a compressed, cacheable asset served from our origin.
  const ogImage = service.mobileImageUrl
    ? `${CANONICAL_ORIGIN}/_next/image?url=${encodeURIComponent(service.mobileImageUrl)}&w=1200&q=75`
    : `${CANONICAL_ORIGIN}/services/blowout.jpg`
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `${service.name} — GLAMSQUAD`,
    description,
    provider: { '@type': 'Organization', name: 'Glamsquad', url: CANONICAL_ORIGIN },
    areaServed: { '@type': 'Country', name: 'United States' },
    serviceType: service.name,
    offers: {
      '@type': 'Offer', price: service.price, priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
  }

  return (
    <Page>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={ogImage} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema).replace(/</g, '\\u003c') }}
        />
      </Head>

      <HeroBookingCard service={service} availableCities={availableCities} addOnCount={visibleAddOns.length} />
      <FeaturedAddons service={service} featuredAddOns={visibleAddOns} />
      <BeautyProsYouCanTrust />
      <ThingsToKnow items={service.thingsToKnow || []} />
      <CrossSell services={visibleCrossSell} fromSlug={service.slug} />
      <ReviewsSection reviews={reviews} />
      <FaqSection faqs={faqs} />
      <AppDownload />
    </Page>
  )
}

// Page wrapper. Full viewport width with a centered column so each section caps at
// max-width: 1600px and stays centered on large screens, matching the HomeWrapper
// pattern — rather than relying on the app-wide <main> to be full-width.
const Page = styled.div`
  width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
`
